import { defaultLocale, type Locale } from "@/i18n/config";

// English is the synchronous fallback for every lookup below, so it is the one locale that must be
// bundled statically.
import en from "@/messages/en.json";

// Spanish used to be statically imported here too, which put 178 KB of Spanish JSON into the client
// bundle of every English user (en.json is a further 171 KB). It is now loaded on demand by
// preloadTranslation() — exactly the same path the other 73 locales in src/messages already use.
// Until it resolves, lookups fall back to English, which is the existing behaviour for every other
// non-English locale.

// Type for translation keys
export type TranslationKey = keyof typeof en;

// Type for translations - allows partial translations (missing keys will fallback to English)
// Using a more permissive type that allows any structure, since translations may be incomplete
type TranslationData = Record<string, any>;

// Translation files map - locales are added dynamically by preloadTranslation()
const translations: Record<string, TranslationData> = {
  en,
};

// Preload a translation file and cache it
// forceReload: if true, will reload even if already cached (useful when switching languages)
export async function preloadTranslation(locale: Locale, forceReload: boolean = false): Promise<void> {
  if (translations[locale] && !forceReload) {
    return; // Already loaded and not forcing reload
  }

  // Clear cache if forcing reload
  if (forceReload && translations[locale]) {
    delete translations[locale];
  }

  // First, try to load from static import (for bundled translations)
  try {
    const translation = await import(`@/messages/${locale}.json`);
    translations[locale] = translation.default || translation;
    
    // Verify it was loaded
    if (process.env.NODE_ENV === "development") {
      console.log(`[i18n] Successfully loaded translation for ${locale} from static import`);
    }
    return;
  } catch (error) {
    // Static import failed - file doesn't exist in bundle
    // Continue to try fetching from API
    if (process.env.NODE_ENV === "development") {
      console.log(`[i18n] Static import failed for ${locale}, trying API...`);
    }
  }

  // If static import failed, try to fetch from API (for dynamically generated translations)
  if (typeof window !== "undefined") {
    try {
      // Use cache busting for force reload
      const cacheBuster = forceReload ? `?t=${Date.now()}` : '';
      const fetchResponse = await fetch(`/api/i18n/translations/${locale}${cacheBuster}`, {
        cache: "no-store", // Always fetch fresh
        credentials: "include", // Include cookies for authentication if needed
      });

      if (fetchResponse.ok) {
        const translation = await fetchResponse.json();
        translations[locale] = translation;
        
        if (process.env.NODE_ENV === "development") {
          console.log(`[i18n] Successfully loaded translation for ${locale} from API`);
        }
        return;
      }
      /* No generation fallback any more. Every locale ships as a static JSON file in
         src/messages, so a miss here means a genuinely unknown locale, and English is
         the right answer for that rather than a paid translation call. */
    } catch (apiError) {
      // API call failed (network error, etc.)
      if (process.env.NODE_ENV === "development") {
        console.log(`[i18n] Error fetching translation for ${locale} from API:`, apiError);
      }
    }
  }
  
  // Fall back to English - ensure it's always set
  if (process.env.NODE_ENV === "development") {
    console.log(`[i18n] Falling back to English for ${locale}`);
  }
  // Make sure we have a reference to English translations, not a copy
  if (!translations[defaultLocale]) {
    translations[defaultLocale] = en;
  }
  translations[locale] = translations[defaultLocale];
}

// Lazy load other languages - synchronous version that checks cache first
function loadTranslationSync(locale: string): TranslationData {
  if (translations[locale]) {
    return translations[locale];
  }
  // If not in cache, return English (will be loaded async)
  return translations[defaultLocale];
}

// Lazy load other languages - async version
async function loadTranslation(locale: string): Promise<TranslationData> {
  if (translations[locale]) {
    return translations[locale];
  }

  try {
    // Try to import the translation file
    const translation = await import(`@/messages/${locale}.json`);
    translations[locale] = translation.default;
    return translation.default;
  } catch (error) {
    console.warn(`[i18n] Translation file for ${locale} not found, falling back to English`);
    return translations[defaultLocale];
  }
}

/**
 * Get translation for a key
 */
export async function getTranslationAsync(
  locale: Locale,
  key: string,
  params?: Record<string, string | number>
): Promise<string> {
  const translation = await loadTranslation(locale);
  const keys = key.split(".");
  let value: any = translation;

  // Navigate through nested keys
  for (const k of keys) {
    value = value?.[k];
    if (value === undefined) {
      // Fallback to English
      value = translations[defaultLocale];
      for (const fallbackKey of keys) {
        value = value?.[fallbackKey];
      }
      break;
    }
  }

  if (typeof value !== "string") {
    return key; // Return key if translation not found
  }

  // Replace parameters
  if (params) {
    return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
      return params[paramKey]?.toString() || match;
    });
  }

  return value;
}

/**
 * Synchronous version - uses cached translations
 * If translation not loaded yet, tries to load it synchronously from cache or falls back to English
 */
export function getTranslation(
  locale: Locale,
  key: string,
  params?: Record<string, string | number>
): string {
  const keys = key.split(".");
  
  // Use the locale's translation if available, otherwise fall back to English
  // Check if translation exists in cache
  const translation = translations[locale] || translations[defaultLocale];
  
  if (process.env.NODE_ENV === "development" && locale !== defaultLocale && !translations[locale]) {
    // Log when falling back to English (only in dev)
    console.log(`[i18n] Translation for ${locale} not in cache, using English fallback for key: ${key}`);
  }
  
  let value: any = translation;
  
  // Navigate through nested keys
  for (const k of keys) {
    value = value?.[k];
    if (value === undefined) {
      // Fallback to English
      value = translations[defaultLocale];
      for (const fallbackKey of keys) {
        value = value?.[fallbackKey];
      }
      break;
    }
  }

  // Never return raw key to user - always prefer English fallback for missing translations
  if (typeof value !== "string") {
    // Return user-friendly fallback instead of raw key
    // For onboarding keys, provide readable defaults
    if (key.startsWith("onboarding.")) {
      const keyName = key.split(".").pop() || key;
      const fallbacks: Record<string, string> = {
        "title": "Welcome to GrowthSync",
        "letsGetStarted": "Let's set up your profile so you can start connecting with entrepreneurs, founders and business owners who share your goals.",
        "setupTakesTwoMinutes": "The first few steps take about two minutes. Everything after that is optional and can wait.",
        "skipForNow": "Skip for now",
        "whatYoullSetUp": "What you'll set up:",
        "basicInfoItem": "Basic information (name, headline, location)",
        "businessDetailsItem": "Business details (stage, team size, industries)",
        "bioAndLookingForItem": "Your bio and what you're looking for",
        "profilePromptsItem": "Profile prompts to showcase your personality",
        "linksItem": "Links to your website and social profiles",
        "step": "Step",
        "complete": "Complete",
        "back": "Back",
        "nextStep": "Next Step",
        "welcome": "Welcome to GrowthSync",
        "getStarted": "Let's get started",
        "createAccount": "Create your account",
        "completeProfile": "Complete your profile",
        "startConnecting": "Start connecting",
        "stepByStep": "We'll guide you through each step",
        "dataPrivacy": "Your privacy is important to us. We only collect the information necessary to help you connect with the right people.",
        "secureAccount": "Your account is secure. We use industry-standard encryption to protect your data.",
        "communityGuidelines": "By joining, you agree to follow our community guidelines and treat all members with respect.",
        "whyWeAsk": "Why we ask this",
        "whyWeAskDescription": "This information helps us match you with the right people and opportunities.",
        "whatHappensNext": "What happens next",
        "whatHappensNextDescription": "Once you complete your profile, you'll be able to discover and connect with entrepreneurs, founders and business owners.",
        "youCanChangeLater": "You can change this later",
        "youCanChangeLaterDescription": "Don't worry about getting everything perfect. You can update any information in your profile settings at any time.",
        "valueExplanation": "A complete profile helps you make better connections and increases your visibility in the community.",
        "valueExplanationDescription": "Members with complete profiles receive more profile views and connection requests.",
        "loadingProfile": "Loading profile...",
        "profileSetUpSuccess": "Welcome to GrowthSync! Your profile is set up.",
        "failedToSaveProfile": "Failed to save profile. Please try again.",
        "saving": "Saving...",
        "completeSetup": "Complete setup",
        "photoUpdatedSuccessfully": "Photo updated successfully",
        "adjustYourPhoto": "Adjust your photo",
        "confirm": "Confirm",
        "cancel": "Cancel",
        "basicInformation": "Basic Information",
        "basicInformationDescription": "Tell others who you are",
      };
      
      // Handle step with parameters
      if (keyName === "step" && params) {
        return `Step ${params.current} of ${params.total}`;
      }
      
      return fallbacks[keyName] || key;
    }
    // Payments keys - ensure user-facing text when lookup fails
    const paymentsFallbacks: Record<string, string> = {
      "payments.alreadySubscribed": "You're already subscribed to this plan.",
      "payments.alreadySubscribedOpeningPortal": "You're already subscribed to this plan. Opening billing portal.",
      "payments.alreadySubscribedPlanUpToDate": "You're already subscribed to this plan. Your plan is up to date.",
    };
    if (paymentsFallbacks[key]) {
      return paymentsFallbacks[key];
    }
    return key; // Return key if translation not found
  }
  
  // If value is a string but looks like a raw key (e.g., "onboarding.title" or just "title")
  // Provide fallback for onboarding keys to ensure user-friendly text
  if (typeof value === "string" && key.startsWith("onboarding.")) {
    const keyName = key.split(".").pop() || key;
    // Check if the value looks like a raw key (contains dots or equals the key name)
    if (value.includes(".") || value === keyName || value === key) {
      const fallbacks: Record<string, string> = {
        "title": "Welcome to GrowthSync",
        "letsGetStarted": "Let's set up your profile so you can start connecting with entrepreneurs, founders and business owners who share your goals.",
        "setupTakesTwoMinutes": "The first few steps take about two minutes. Everything after that is optional and can wait.",
        "skipForNow": "Skip for now",
        "whatYoullSetUp": "What you'll set up:",
        "basicInfoItem": "Basic information (name, headline, location)",
        "businessDetailsItem": "Business details (stage, team size, industries)",
        "bioAndLookingForItem": "Your bio and what you're looking for",
        "profilePromptsItem": "Profile prompts to showcase your personality",
        "linksItem": "Links to your website and social profiles",
        "step": params ? `Step ${params.current} of ${params.total}` : "Step",
        "complete": "Complete",
        "back": "Back",
        "nextStep": "Next Step",
        "loadingProfile": "Loading profile...",
        "profileSetUpSuccess": "Welcome to GrowthSync! Your profile is set up.",
        "failedToSaveProfile": "Failed to save profile. Please try again.",
        "saving": "Saving...",
        "completeSetup": "Complete setup",
        "photoUpdatedSuccessfully": "Photo updated successfully",
        "adjustYourPhoto": "Adjust your photo",
        "confirm": "Confirm",
        "cancel": "Cancel",
        "basicInformation": "Basic Information",
        "basicInformationDescription": "Tell others who you are",
      };
      
      if (fallbacks[keyName]) {
        // Handle step with parameters if not already handled
        if (keyName === "step" && params) {
          return `Step ${params.current} of ${params.total}`;
        }
        return fallbacks[keyName];
      }
    }
  }

  // Replace parameters
  if (params) {
    return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
      return params[paramKey]?.toString() || match;
    });
  }

  return value;
}

/**
 * Get all translations for a namespace
 */
export function getTranslations(locale: Locale, namespace: string): any {
  const keys = namespace.split(".");
  let value: any = translations[locale] || translations[defaultLocale];

  for (const k of keys) {
    value = value?.[k];
    if (value === undefined) {
      // Fallback to English
      value = translations[defaultLocale];
      for (const fallbackKey of keys) {
        value = value?.[fallbackKey];
      }
      break;
    }
  }

  return value || {};
}


